/**
 * Shared contracts between the CIIMAR API and web front-end.
 * Keep this package framework-agnostic (no runtime deps) so it can be
 * consumed by both the NestJS backend and the React front-end.
 */

/** Lifecycle of an onboarding submission (new member intake). */
export enum OnboardingStatus {
  /** Submitted via the invite form, awaiting a manager's review. */
  PendingReview = 'PENDING_REVIEW',
  /** A manager requested corrections; the member was emailed. */
  ChangesRequested = 'CHANGES_REQUESTED',
  /** Approved by a manager, ready to be entered into SISTRADE. */
  Approved = 'APPROVED',
  /** Manually entered into the SISTRADE collaborator record by the office. */
  LaunchedInSistrade = 'LAUNCHED_IN_SISTRADE',
  /** Rejected outright (e.g. spam / duplicate). */
  Rejected = 'REJECTED',
}

/** How an onboarding submission entered the system. */
export enum OnboardingSource {
  /** Entered by an onboarding manager on behalf of the member. */
  Manager = 'MANAGER',
  /** Filled in by the new member via an invite link (/iamnew?token=…). */
  SelfRegistration = 'SELF_REGISTRATION',
}

/**
 * Application roles, resolved from the user's Entra ID (AD-synced) security
 * groups at login. Each maps from a group id/name via AUTH_*_GROUP_IDS:
 *   Portal-Onboarding                → Onboarding
 *   Portal-RH                        → Hr
 *   Portal-ResearchersAndTechnicians → Researcher
 */
export enum Role {
  /** Onboarding office: full management of submissions. */
  Onboarding = 'ONBOARDING',
  /** Human Resources: full management of submissions. */
  Hr = 'HR',
  /** Group-responsible researchers / technicians: create + see their own team. */
  Researcher = 'RESEARCHER',
  /** Centre board/direction: oversight access (not system administration). */
  Direction = 'DIRECTION',
  /** Portal administrator: system settings and integrations (Portal-Admins). */
  Admin = 'ADMIN',
}

/** Roles with full management access (onboarding office + HR + admin). */
export const MANAGER_ROLES: readonly Role[] = [Role.Onboarding, Role.Hr, Role.Admin];

/** Roles with system-administration access (settings, integrations). */
export const ADMIN_ROLES: readonly Role[] = [Role.Admin];

/** Every role that may sign in to the portal in this phase. */
export const PORTAL_ROLES: readonly Role[] = [
  Role.Onboarding,
  Role.Hr,
  Role.Researcher,
  Role.Direction,
  Role.Admin,
];

/** True when the user has full management access. */
export function isManager(roles: readonly Role[]): boolean {
  return roles.some((r) => MANAGER_ROLES.includes(r));
}

/** True when the user is a portal administrator. */
export function isAdmin(roles: readonly Role[]): boolean {
  return roles.includes(Role.Admin);
}

/** Authentication method the deployment is configured to use. */
export enum AuthProviderKind {
  /** Microsoft 365 / Entra ID (OIDC, redirect-based). */
  Entra = 'entra',
  /** Active Directory over LDAP (username/password bind). */
  Ldap = 'ldap',
  /** Local accounts stored in the app (username/password). */
  Local = 'local',
}

/** Public info the login page needs to render the right UI. */
export interface AuthConfigDto {
  provider: AuthProviderKind;
  /**
   * Whether to also offer the local username/password form. True only in
   * staging when a local account exists — lets testers sign in locally even
   * when the primary provider is Microsoft 365. Never true in production.
   */
  localLogin: boolean;
}

/** How a registration invite reaches the new member. */
export enum InviteDeliveryMethod {
  /** The system emails the link to the member. */
  Email = 'EMAIL',
  /** The system returns the link for the requester to share themselves. */
  Link = 'LINK',
  /** The system returns a short alphanumeric code to type at onboarding.ciimar.up.pt. */
  Code = 'CODE',
}

/** Lifecycle of a registration invite. */
export enum InviteStatus {
  /** Created, link is valid, no submission yet. */
  Pending = 'PENDING',
  /** The member submitted the form through this invite. */
  Submitted = 'SUBMITTED',
  /** Cancelled by the requester or a manager. */
  Cancelled = 'CANCELLED',
  /** Past its expiry date without a submission. */
  Expired = 'EXPIRED',
}

/** Payload a requester sends to start a new registration. */
export interface CreateInviteInput {
  deliveryMethod: InviteDeliveryMethod;
  /** Required when deliveryMethod is EMAIL; ignored for LINK / CODE. */
  targetEmail?: string;
  /** The person responsible for the new member (a collaborator id + name). */
  responsiblePersonId?: string;
  responsibleName?: string;
  /** Optional context carried into the submission (future-proofing). */
  researchGroup?: string;
  department?: string;
  note?: string;
}

/** A registration invite as returned to the management / requester UI. */
export interface RegistrationInviteDto {
  id: string;
  deliveryMethod: InviteDeliveryMethod;
  targetEmail?: string | null;
  status: InviteStatus;
  requestedByUsername: string;
  requestedByName: string;
  requestedByEmail?: string | null;
  responsiblePersonId?: string | null;
  responsibleName?: string | null;
  researchGroup?: string | null;
  department?: string | null;
  note?: string | null;
  submissionId?: string | null;
  createdAt: string;
  expiresAt: string;
  submittedAt?: string | null;
  /**
   * The short alphanumeric code, for CODE-delivery invites. Only returned when
   * the invite is first created (so the requester can pass it on).
   */
  code?: string | null;
  /**
   * The public registration URL. Only returned when the invite is first
   * created (so the requester can copy it); never listed afterwards.
   */
  url?: string;
}

/** Public, non-sensitive info shown on the /iamnew form for a valid token. */
export interface InvitePrefill {
  requestedByName: string;
  targetEmail?: string | null;
  researchGroup?: string | null;
  department?: string | null;
}

/**
 * Public payload accepted by the /iamnew registration form. Registration is
 * invite-only: supply either the `token` (from a link/email) or the short
 * `code` (typed at onboarding.ciimar.up.pt).
 */
export interface SelfRegistrationInput {
  token?: string;
  code?: string;
  fullName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  department?: string;
  researchGroup?: string;
  startDate?: string; // ISO date (yyyy-mm-dd)
  notes?: string;
  /** Answers to the admin-curated onboarding fields, keyed by field key. */
  extraFields?: Record<string, string>;
  /** Honeypot field — must stay empty; a filled value flags a bot. */
  website?: string;
  /** CAPTCHA token (e.g. Cloudflare Turnstile), verified server-side. */
  captchaToken?: string;
}

/** A submission as returned to the management UI. */
export interface OnboardingSubmissionDto {
  id: string;
  fullName: string;
  email: string;
  phone?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  researchGroup?: string | null;
  startDate?: string | null;
  notes?: string | null;
  /** Answers to the admin-curated onboarding fields (key → value). */
  extraFields?: Record<string, string> | null;
  status: OnboardingStatus;
  source: OnboardingSource;
  /** AD username of the requester who initiated the invite, if any. */
  requestedByUsername?: string | null;
  requestedByName?: string | null;
  reviewNote?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  /** SISTRADE collaborator id/reference, typed in when entered manually. */
  sistradeReference?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * An authenticated user, resolved from Microsoft Entra ID (Azure AD) at login.
 * `ManagerProfile` is kept as an alias for backwards compatibility.
 */
export interface AuthenticatedUser {
  username: string;
  displayName: string;
  email?: string;
  /** Raw AD/Entra security group ids (object ids) from the token. */
  groups: string[];
  /** Application roles derived from the group ids. */
  roles: Role[];
  /**
   * When set, this session is an IT admin impersonating another user: the value
   * is the real admin (the actor) so the UI can show a banner and stop. Carried
   * in the session token; never present on an ordinary session.
   */
  impersonator?: ImpersonatorInfo;
  /**
   * True when this user is an IT administrator (may impersonate others). Resolved
   * per request from the Admin-IT list; false while impersonating.
   */
  isAdminIt?: boolean;
}

export type ManagerProfile = AuthenticatedUser;

/** The real administrator behind an impersonated session (the "actor"). */
export interface ImpersonatorInfo {
  username: string;
  displayName: string;
  email?: string;
  groups: string[];
  roles: Role[];
}

// ── IT administration: impersonation & the Admin-IT list ──────────────────

/**
 * Roles an IT admin may assume when impersonating a user. Never Admin (system
 * administration) nor the implicit IT-admin capability — impersonation is for
 * reproducing a collaborator's view (investigador/técnico/gestão), not for
 * escalating to another administrator.
 */
export const IMPERSONATABLE_ROLES: readonly Role[] = [
  Role.Researcher,
  Role.Onboarding,
  Role.Hr,
  Role.Direction,
];

/** A member of the IT-admin list (separate from the global Admin profile). */
export interface AdminItMemberDto {
  username: string;
  displayName: string | null;
  /** Display name of whoever added this member (or null when env-seeded). */
  addedBy: string | null;
  createdAt: string;
}

export interface AddAdminItMemberInput {
  username: string;
  displayName?: string | null;
}

/** A person an IT admin may impersonate (from the collaborator roster). */
export interface ImpersonationTargetDto {
  personId: string;
  name: string;
  /** The sign-in username to assume (the person's professional email / UPN). */
  username: string;
  email: string | null;
}

export interface StartImpersonationInput {
  username: string;
  displayName?: string | null;
  /** Roles to assume; must be a subset of IMPERSONATABLE_ROLES. */
  roles: Role[];
}

// ── In-app notifications (derived per-module summary) ──────────────────────

/** One actionable signal within a module (e.g. "submissions pending review"). */
export interface NotificationItemDto {
  /** Stable id for the signal kind (e.g. 'submissions.pending'). */
  kind: string;
  title: string;
  titleEn?: string;
  count: number;
  /** In-app route to open when the item is clicked. */
  link: string;
}

/** A module's notifications, with whether the user has seen the current state. */
export interface NotificationModuleGroupDto {
  module: PortalModule;
  total: number;
  /** True when there is something new since the user last acknowledged it. */
  unseen: boolean;
  items: NotificationItemDto[];
}

export interface NotificationsSummaryDto {
  groups: NotificationModuleGroupDto[];
  total: number;
  unseenTotal: number;
}

export interface MarkNotificationsSeenInput {
  module: PortalModule;
}

/** Aggregate counts for the management dashboard. */
export interface OnboardingStatsDto {
  total: number;
  pendingReview: number;
  changesRequested: number;
  /** Approved and awaiting manual entry into SISTRADE. */
  approved: number;
  launched: number;
  rejected: number;
}

/** A SISTRADE collaborator record (normalised for our UI). Phase 2+. */
export interface SistradeCollaborator {
  id: string;
  fullName: string;
  email?: string;
  department?: string;
  jobTitle?: string;
  active: boolean;
}

/** Status of the SISTRADE integration (for the settings page). */
export interface SistradeStatusDto {
  mode: 'stub' | 'rest' | 'sql' | 'hybrid';
  /** True when the active mode has everything it needs to run. */
  configured: boolean;
  baseUrl?: string;
}

/** Result of a pull-from-SISTRADE synchronization. */
export interface SistradeSyncResultDto {
  /** 'collaborators' = full ficha (+ INFAD); 'employees' = lean fallback. */
  source: 'employees' | 'collaborators';
  total: number;
  created: number;
  updated: number;
  skipped: number;
  /** Field values written (full-ficha pull only). */
  fields?: number;
}

/** State of the background SISTRADE sync job (started, then polled). */
export type SistradeSyncState = 'idle' | 'running' | 'success' | 'error';
export interface SistradeSyncStatusDto {
  state: SistradeSyncState;
  startedAt?: string;
  finishedAt?: string;
  /** The result of the last finished run (when state is 'success'). */
  result?: SistradeSyncResultDto;
  /** The error message of the last run (when state is 'error'). */
  error?: string;
}

// ── Collaborator record: the configurable field engine ─────────────────────

/** The type of a collaborator-record field (built-in or manager-created). */
export enum FieldType {
  Text = 'TEXT',
  LongText = 'LONG_TEXT',
  Number = 'NUMBER',
  Integer = 'INTEGER',
  Date = 'DATE',
  DateTime = 'DATETIME',
  Boolean = 'BOOLEAN',
  Select = 'SELECT',
  MultiSelect = 'MULTISELECT',
  Email = 'EMAIL',
  Phone = 'PHONE',
  Url = 'URL',
  Currency = 'CURRENCY',
  Percent = 'PERCENT',
}

/** Types whose values are constrained to a fixed option list. */
export const OPTION_FIELD_TYPES: readonly FieldType[] = [FieldType.Select, FieldType.MultiSelect];

/** One selectable option for a SELECT / MULTISELECT field. */
export interface FieldOption {
  value: string;
  label: string;
  /** English label; falls back to `label` when absent. */
  labelEn?: string | null;
}

/** A logical group of fields (a tab/section of the ficha). */
export interface FieldSectionDto {
  key: string;
  label: string;
  /** English label; falls back to `label` when absent. */
  labelEn?: string | null;
  order: number;
  /** When true, the whole section is reserved (all its fields are reserved). */
  reserved: boolean;
}

/** Patch for a section (currently its reserved classification). */
export interface UpdateFieldSectionInput {
  reserved?: boolean;
  label?: string;
  labelEn?: string | null;
  order?: number;
}

export interface CreateFieldSectionInput {
  label: string;
  labelEn?: string | null;
}

/** A field definition — a built-in (seeded, locked) or custom (manager) field. */
export interface FieldDefinitionDto {
  id: string;
  sectionKey: string;
  key: string;
  label: string;
  /** English label; falls back to `label` when absent. */
  labelEn?: string | null;
  type: FieldType;
  options: FieldOption[];
  required: boolean;
  order: number;
  /** Built-in fields cannot be deleted or retyped, only reordered/required. */
  builtIn: boolean;
  active: boolean;
  /** When true, only users with VIEW_RESERVED see this field's value. */
  reserved: boolean;
  /** When true, the value is picked from an existing reference list (dropdown). */
  reference?: boolean;
  /** When true, the field appears on the onboarding intake form. */
  onboarding?: boolean;
  /** When true, the value must be unique across collaborators (NIF, CC, NISS…). */
  uniqueValue?: boolean;
}

/** The whole ficha schema: sections, each with its ordered fields. */
export interface FieldsSchemaDto {
  sections: (FieldSectionDto & { fields: FieldDefinitionDto[] })[];
}

/**
 * A field as shown on the public onboarding intake form. Deliberately a
 * reduced, non-sensitive view of a field definition: no ids, no reserved
 * flag, and options only for the field's own fixed list — reference fields
 * never expose SISTRADE values on the public form (rendered as free text).
 */
export interface OnboardingFieldDto {
  key: string;
  label: string;
  type: FieldType;
  /** Required on the onboarding form is inherited from the general config. */
  required: boolean;
  /** Options for SELECT/MULTISELECT fields; empty otherwise. */
  options: FieldOption[];
  /** Label of the section the field belongs to (for grouping on the form). */
  sectionLabel: string;
  /** English field label; falls back to `label` when absent. */
  labelEn?: string | null;
  /** English section label; falls back to `sectionLabel` when absent. */
  sectionLabelEn?: string | null;
}

/** Payload to create a custom field (Onboarding/RH). */
export interface CreateFieldDefinitionInput {
  sectionKey: string;
  label: string;
  labelEn?: string | null;
  type: FieldType;
  options?: FieldOption[];
  required?: boolean;
  reserved?: boolean;
  onboarding?: boolean;
}

/** Payload to update a field definition (custom fully; built-in limited). */
export interface UpdateFieldDefinitionInput {
  label?: string;
  labelEn?: string | null;
  options?: FieldOption[];
  required?: boolean;
  order?: number;
  active?: boolean;
  /** Reserved classification — allowed on built-in fields too. */
  reserved?: boolean;
  /** Show on the onboarding intake form — allowed on built-in fields too. */
  onboarding?: boolean;
  /** Move the field to another section (allowed on built-in fields too). */
  sectionKey?: string;
}

// ── Collaborator record: Person ────────────────────────────────────────────

/** Whether a collaborator is currently active at CIIMAR. */
export enum PersonStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
}

/** A person's core, queried fields (the rest live in the field engine). */
export interface PersonFieldHistoryDto {
  id: string;
  fieldKey: string;
  fieldLabel: string;
  oldValue: string | null;
  newValue: string | null;
  source: string;
  changedAt: string;
}

export interface PersonSummaryDto {
  id: string;
  code?: string | null;
  fullName: string;
  shortName?: string | null;
  personalEmail?: string | null;
  professionalEmail?: string | null;
  /** The email to display, per the person's "Email por defeito" (default: professional). */
  preferredEmail?: string | null;
  status: PersonStatus;
  updatedAt: string;
  /** Enriched list fields (resolved names/values from the field engine). */
  departamento?: string | null;
  situacaoColaborador?: string | null;
  /** ISO date (yyyy-mm-dd) or null. */
  contractStart?: string | null;
  contractEnd?: string | null;
  /** Dedication percentage (number) or null. */
  dedicationPercent?: number | null;
  /** True when any required ficha field has no value (data em falta). */
  missingRequired?: boolean;
  /** Colaborador Externo (Dados do Contrato) = Sim. */
  external?: boolean;
}

/** One month in a time series: 'yyyy-mm' key + count. */
export interface MonthCountDto {
  /** 'yyyy-mm'. */
  month: string;
  count: number;
}

/** One month in the year-over-year contracts comparison. */
export interface YearComparePointDto {
  /** 1–12. */
  month: number;
  /** Contracts started in that month, previous year. */
  previous: number;
  /** Current year: actual up to the current month, forecast afterwards. */
  current: number;
  /** True when `current` is a forecast (a month still ahead this year). */
  forecast: boolean;
}

/** Collaborator-focused analytics for the Painel (dashboard). */
export interface PeopleAnalyticsDto {
  /** Colaboradores ativos. */
  totalActive: number;
  /** Ativos com Grau de Ensino = Doutoramento. */
  totalDoctorate: number;
  /** Ativos com Cargo = Administrative Services. */
  totalAdminServices: number;
  /** Últimos 12 meses — contratos iniciados por mês. */
  contractsStartedByMonth: MonthCountDto[];
  /** Últimos 12 meses — contratos terminados por mês. */
  contractsEndedByMonth: MonthCountDto[];
  /** Ano anterior vs atual (contratos iniciados), com previsão. */
  yearComparison: YearComparePointDto[];
  /** ISO timestamp of when this snapshot was computed. */
  generatedAt: string;
}

// ── Painel builder (configurable dashboard) ──────────────────────────────

/** Kind of card the Painel builder can place. */
export enum DashboardCardType {
  /** A total: count of collaborators matching a field filter. */
  Count = 'count',
  /** One of the contract charts. */
  Chart = 'chart',
}

/**
 * Which chart a chart card shows. People charts pull their series from the
 * fixed PeopleAnalyticsDto; article charts carry their series on the resolved
 * card (`chartData`).
 */
export type DashboardChartKind =
  // People (collaborators)
  | 'started'
  | 'ended'
  | 'yearCompare'
  // Free-form distribution of collaborators by any ficha field (group-by)
  | 'fieldDistribution'
  // Publications (articles)
  | 'pubsByYear'
  | 'pubsByMonth'
  | 'pubsYearCompare'
  | 'quartileDist'
  | 'pubsByTeam'
  | 'topResearchers'
  | 'citationsByYear';

/** Visual style for a distribution chart. */
export type DashboardChartStyle = 'bar' | 'donut' | 'hbar';

/** A publications KPI metric (total/ratio over the article mirror). */
export type ArticleMetric =
  | 'count'
  | 'citations'
  | 'citationsPerPub'
  | 'percentQ1'
  | 'percentQ1Q2'
  | 'percentOpenAccess'
  | 'researchers'
  | 'pubsPerResearcher'
  // Current year + projection
  | 'pubsThisYear'
  | 'pubsGrowthRate';

/** Match operator for a count card. 'ne' = present but equal to none of the values. */
export type DashboardCountOp = 'eq' | 'contains' | 'ne';

/** Count card: how many collaborators match `field` `op` `value(s)`. */
export interface DashboardCountConfig {
  /** Field key to match ('status' for the core situation, else a ficha field key). */
  field: string;
  /** Value to match against (resolved name as stored), case-insensitive. */
  value: string;
  /**
   * Optional additional values; the match succeeds when the field matches ANY of
   * `value`+`values` (for 'ne', when it matches NONE). Lets a card mean e.g.
   * "nationality is Portugal or Spain".
   */
  values?: string[];
  /** Match mode (default 'eq'). */
  op?: DashboardCountOp;
  /** Count only active collaborators (default true). */
  activeOnly?: boolean;
}

/** Count card over the publications mirror (a total or ratio). */
export interface DashboardArticleConfig {
  metric: ArticleMetric;
}

export interface DashboardChartConfig {
  chart: DashboardChartKind;
  /** The ficha field to group by, when chart = 'fieldDistribution'. */
  field?: string | null;
  /** Visual style for a distribution chart (default 'bar'). */
  style?: DashboardChartStyle | null;
  /** Count only active collaborators for a distribution chart (default true). */
  activeOnly?: boolean | null;
}

/** Who a card is shown to: a role, an AD group, or a specific person. */
export type DashboardSubjectType = 'role' | 'group' | 'person';

/** A configured Painel card (admin builder), before its value is computed. */
export interface DashboardCardDto {
  id: string;
  /** Visibility target. */
  subjectType: DashboardSubjectType;
  /** Role value, AD group id, or Person id, per subjectType. */
  subjectId: string;
  /** @deprecated legacy role target; mirrors subjectId when subjectType='role'. */
  role?: Role | null;
  position: number;
  type: DashboardCardType;
  label: string;
  /** English label; falls back to `label` when absent. */
  labelEn?: string | null;
  tone?: string | null;
  icon?: string | null;
  /** Present when type = Count and the source is collaborators. */
  count?: DashboardCountConfig | null;
  /** Present when type = Count and the source is publications. */
  article?: DashboardArticleConfig | null;
  /** Present when type = Chart. */
  chart?: DashboardChartConfig | null;
}

/** A Painel card resolved for rendering (count cards carry their computed value). */
export interface ResolvedDashboardCardDto extends DashboardCardDto {
  value?: number;
  /** A unit/suffix for the value (e.g. '%'), set for some article metrics. */
  unit?: string | null;
  /** Series for an article chart card (people charts use PeopleAnalyticsDto). */
  chartData?: { label: string; value: number }[];
  /** Year-over-year series for the 'pubsYearCompare' article chart (with forecast). */
  yearCompare?: YearComparePointDto[];
}

/** One card in a save request (no id — the whole set for a subject is replaced). */
export interface DashboardCardInput {
  type: DashboardCardType;
  label: string;
  labelEn?: string | null;
  tone?: string | null;
  icon?: string | null;
  count?: DashboardCountConfig | null;
  article?: DashboardArticleConfig | null;
  chart?: DashboardChartConfig | null;
}

/** Replace the whole Painel card set for one subject (role / group / person). */
export interface SaveDashboardCardsInput {
  subjectType: DashboardSubjectType;
  subjectId: string;
  cards: DashboardCardInput[];
}

/** The targets the builder can assign cards to. */
export interface DashboardSubjectsDto {
  roles: { id: string; label: string }[];
  groups: { id: string; label: string }[];
  people: { id: string; label: string }[];
}

/** Per-widget size on the personal Painel (discrete, so tiles stay framed). */
export type DashboardWidgetSize = 'sm' | 'md' | 'lg';

/** One tile in a user's personal Painel arrangement (order + size). */
export interface DashboardLayoutItem {
  /** Widget id: a built-in (e.g. 'kpi.active', 'chart.started', 'panel.pending')
   *  or a custom card ('custom.<cardId>'). */
  id: string;
  size: DashboardWidgetSize;
}

/** A user's saved Painel arrangement; empty `items` means "use the default". */
export interface DashboardLayoutDto {
  items: DashboardLayoutItem[];
}

export interface SaveDashboardLayoutInput {
  items: DashboardLayoutItem[];
}

/** A field the builder can filter on, with its known values (for dropdowns). */
export interface DashboardFilterFieldDto {
  key: string;
  label: string;
  values: string[];
}

/** A soft-deleted collaborator, for the admin-only deleted bin. */
export interface DeletedPersonDto {
  id: string;
  code?: string | null;
  fullName: string;
  status: PersonStatus;
  /** ISO timestamp. */
  createdAt: string;
  /** ISO timestamp of deletion. */
  deletedAt: string;
  /** Display name of who deleted it. */
  deletedBy?: string | null;
}

/** A collaborator with a name identical/similar to a query (duplicate check). */
export interface SimilarPersonDto {
  id: string;
  fullName: string;
  code?: string | null;
  status: PersonStatus;
}

/** One field value on a person's record. */
export interface FieldValueDto {
  definitionId: string;
  value: unknown;
}

/** The full collaborator record: core columns + all field values. */
export interface PersonDetailDto {
  id: string;
  code?: string | null;
  fullName: string;
  shortName?: string | null;
  personalEmail?: string | null;
  professionalEmail?: string | null;
  status: PersonStatus;
  orcid?: string | null;
  cienciaId?: string | null;
  scopusAuthorId?: string | null;
  createdAt: string;
  updatedAt: string;
  values: FieldValueDto[];
}

/** Core columns accepted when creating a person. */
export interface CreatePersonInput {
  fullName: string;
  code?: string;
  shortName?: string;
  personalEmail?: string;
  professionalEmail?: string;
  status?: PersonStatus;
  orcid?: string;
  cienciaId?: string;
  scopusAuthorId?: string;
}

export type UpdatePersonInput = Partial<CreatePersonInput>;

/** One field value to set on a person (value shape depends on the field type). */
export interface SetFieldValueInput {
  definitionId: string;
  /** Omitted / null clears the value. */
  value?: unknown;
}

// ── Permissions ────────────────────────────────────────────────────────────

/** Fine-grained capabilities beyond the coarse role gates. */
export enum Permission {
  /** See fields/sections marked as reserved on a collaborator's record. */
  ViewReserved = 'VIEW_RESERVED',
}

/** Whom a permission grant applies to. */
export enum PermissionSubjectType {
  Role = 'ROLE',
  User = 'USER',
  Group = 'GROUP',
}

/** A grant either allows or denies; DENY always wins. */
export enum PermissionEffect {
  Allow = 'ALLOW',
  Deny = 'DENY',
}

export interface PermissionGrantDto {
  id: string;
  subjectType: PermissionSubjectType;
  /** Role value, username, or AD group id/name depending on subjectType. */
  subjectId: string;
  permission: Permission;
  effect: PermissionEffect;
}

export interface CreatePermissionGrantInput {
  subjectType: PermissionSubjectType;
  subjectId: string;
  permission: Permission;
  effect: PermissionEffect;
}

/** The current user's effective capabilities (for the UI to adapt). */
export interface EffectivePermissionsDto {
  viewReserved: boolean;
}

// ── Module access (permission-driven navigation) ───────────────────────────

/**
 * Portal modules that can be granted to a person. Everyone additionally has a
 * base "Meu Perfil" (their own ficha), so it is not listed here.
 */
export enum PortalModule {
  Colaboradores = 'colaboradores',
  Onboarding = 'onboarding',
  Rh = 'rh',
  SafeLab = 'safelab',
  Permissoes = 'permissoes',
  ActiveDirectory = 'active_directory',
  Definicoes = 'definicoes',
  /** Generic/global dashboard (not the Onboarding-only dashboard). */
  Dashboard = 'dashboard',
  /** Vehicles / parking management (who needs a parking spot). */
  Viaturas = 'viaturas',
  /** Teams & research lines (read-only mirror of SISTRADE). */
  Equipa = 'equipa',
  /** Scientific output (articles) mirrored from the Elsevier/Scopus API. */
  Artigos = 'artigos',
  /** Daily attendance records (from an internal API or SQL database). */
  Presencas = 'presencas',
}

// ── Attendance (Registo Presenças) — external daily records ────────────────

/** Where the attendance records come from. */
export type AttendanceSourceMode = 'off' | 'sql' | 'api';

/** Health/readiness of the attendance integration (drives the module state). */
export interface AttendanceStatusDto {
  /** Configured source mode ('off' until a provider is set up). */
  mode: AttendanceSourceMode;
  /** True when the source connection details are present. */
  configured: boolean;
  /**
   * True when a collaborator can be matched to the records source. The match id
   * (webapp ↔ records DB) is not yet collected from SISTRADE, so this stays
   * false until that mapping exists.
   */
  linked: boolean;
  /** A human-readable state message (pt-PT). */
  message: string;
}

/** One daily attendance entry (placeholder shape, refined once linked). */
export interface AttendanceRecordDto {
  /** ISO date (YYYY-MM-DD). */
  date: string;
  /** First clock-in time of the day (HH:mm) or null. */
  checkIn: string | null;
  /** Last clock-out time of the day (HH:mm) or null. */
  checkOut: string | null;
  /** Source status/label for the day (e.g. presença, falta, férias). */
  status: string | null;
}

/** The attendance view for a collaborator: integration state + records. */
export interface AttendanceOverviewDto {
  status: AttendanceStatusDto;
  records: AttendanceRecordDto[];
}

export const ALL_MODULES: readonly PortalModule[] = Object.values(PortalModule);

/** A person's access to one module. */
export interface ModuleAccessDto {
  module: PortalModule;
  canEdit: boolean;
}

/** A module grant row (admin view of who can access what). */
export interface ModuleGrantDto {
  id: string;
  personId: string;
  personName: string;
  /** The ficha `user_name` the login is matched against (may be unset). */
  userName: string | null;
  module: PortalModule;
  canEdit: boolean;
  createdAt: string;
}

export interface CreateModuleGrantInput {
  personId: string;
  module: PortalModule;
  canEdit: boolean;
}
export interface UpdateModuleGrantInput {
  canEdit: boolean;
}

/**
 * The current user's resolved access ("os meus módulos"), computed at login
 * from their roles + the person's module grants. Everyone implicitly has
 * "Meu Perfil"; `modules` holds the extra modules they may open.
 */
export interface MyAccessDto {
  /** Resolved Person id, or null when the login can't be matched to a ficha. */
  personId: string | null;
  personName: string | null;
  isAdmin: boolean;
  /**
   * True when the user is an IT administrator (not impersonating). The
   * Definições area is reserved to IT admins — it is the core configuration of
   * the webapp — so the frontend gates it on this, not on `isAdmin`.
   */
  isAdminIt: boolean;
  modules: ModuleAccessDto[];
}

// ── Active Directory provisioning (config-driven) ──────────────────────────

/** Maps a ficha field (core column key or field slug) to an AD attribute. */
export interface AdAttributeMappingDto {
  id: string;
  /** Ficha field key: a core key (e.g. "fullName") or a field slug. */
  fieldKey: string;
  /** AD/LDAP attribute name (e.g. "telephoneNumber", "department", "title"). */
  adAttribute: string;
  order: number;
  active: boolean;
}

export interface CreateAdAttributeMappingInput {
  fieldKey: string;
  adAttribute: string;
}
export interface UpdateAdAttributeMappingInput {
  adAttribute?: string;
  order?: number;
  active?: boolean;
}

/** Admin-editable AD settings (non-secret; connection details stay in env). */
export interface AdSettingsDto {
  /** Distinguished name of the OU new accounts are created in. */
  defaultOu: string;
  /** UPN suffix appended to the username (e.g. "ciimar.up.pt"). */
  upnSuffix: string;
  /**
   * Ordered username patterns; the first that yields an available username
   * wins. Tokens: {f} first initial, {l} last initial, {first} first name,
   * {last} last name. Never add numeric suffixes.
   */
  usernamePatterns: string[];
}
export type UpdateAdSettingsInput = Partial<AdSettingsDto>;

export interface AdStatusDto {
  mode: 'stub' | 'ldaps';
  /** True when the active mode has everything it needs to run. */
  configured: boolean;
  url?: string;
}

export interface UsernameSuggestionInput {
  firstName: string;
  lastName: string;
}
export interface UsernameSuggestionDto {
  /** null when no configured pattern produced an available username. */
  username: string | null;
  /** The pattern that produced it. */
  pattern?: string;
}

// ── Viaturas / Estacionamento ────────────────────────────────────────────

/** Parking zone for a vehicle assignment. */
export enum ParkingZone {
  Internal = 'interno',
  External = 'externo',
}

/** A collaborator's vehicle + parking assignment (one record per vehicle). */
export interface ParkingAssignmentDto {
  id: string;
  /** The collaborator this vehicle belongs to. */
  personId: string;
  personName?: string | null;
  personCode?: string | null;
  /** Matrícula. */
  plate: string;
  /** Marca/modelo (optional). */
  vehicle?: string | null;
  zone: ParkingZone;
  /** Local (ex.: Sede, Polo IPMA). */
  location: string;
  /** Ids dos cartões físicos a colocar visíveis na viatura. */
  cardIds: string[];
  notes?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateParkingAssignmentInput {
  personId: string;
  plate: string;
  vehicle?: string | null;
  zone: ParkingZone;
  location: string;
  cardIds?: string[];
  notes?: string | null;
  active?: boolean;
}

export type UpdateParkingAssignmentInput = Partial<CreateParkingAssignmentInput>;

// ── Configuração Global (instituição + locais + branding) ─────────────────

/** A physical location of the institution (feeds e.g. the Viaturas "Local"). */
export interface InstitutionLocationDto {
  id: string;
  name: string;
  /** Sigla / abreviatura (ex.: "IPMA"). */
  abbreviation?: string | null;
  /** Morada do local. */
  address?: string | null;
  sortOrder: number;
}

export interface CreateInstitutionLocationInput {
  name: string;
  abbreviation?: string | null;
  address?: string | null;
}

export type UpdateInstitutionLocationInput = Partial<CreateInstitutionLocationInput>;

/** The institution's global settings (admin view). */
export interface InstitutionSettingsDto {
  name?: string | null;
  nif?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  /** Data URLs (base64) or null. */
  logo?: string | null;
  icon?: string | null;
  loginBackground?: string | null;
  /** Heading shown above the custom Painel cards (admin-editable, bilingual). */
  dashboardSectionLabel?: string | null;
  dashboardSectionLabelEn?: string | null;
  locations: InstitutionLocationDto[];
}

export interface UpdateInstitutionSettingsInput {
  name?: string | null;
  nif?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  dashboardSectionLabel?: string | null;
  dashboardSectionLabelEn?: string | null;
}

/** Branding served publicly (before login), for the login screen + favicon. */
export interface InstitutionBrandingDto {
  name?: string | null;
  logo?: string | null;
  icon?: string | null;
  loginBackground?: string | null;
  /** Heading for the custom Painel cards section; read app-wide. */
  dashboardSectionLabel?: string | null;
  dashboardSectionLabelEn?: string | null;
}

/** The three configurable brand assets. */
export type BrandAssetKind = 'logo' | 'icon' | 'loginBackground';

// ── Machine translation (admin-defined labels) ─────────────────────────────

/** Languages the portal translates between. */
export type TranslationLang = 'pt' | 'en';

/** Request to machine-translate one or more admin-defined labels. */
export interface TranslateTextInput {
  texts: string[];
  to: TranslationLang;
  from?: TranslationLang;
}

/** Result of a translation request; `configured` is false when no engine is set. */
export interface TranslateTextResult {
  translations: string[];
  configured: boolean;
}

/** Result of bulk-filling the missing English labels of the ficha schema. */
export interface TranslateMissingResult {
  configured: boolean;
  /** How many labels (fields, sections, options) got an English translation. */
  translated: number;
}

// ── Equipa (equipas + linhas de investigação; espelho do SISTRADE) ────────

/** A team member (PI / CO-PI / Resource) with status, dedication, entry date. */
export interface TeamMemberDto {
  code: string;
  name: string;
  statusCode?: string | null;
  statusDescription?: string | null;
  statusAbbreviation?: string | null;
  dedicationPercentage?: number | null;
  /** ISO date or null. */
  entryDate?: string | null;
}

/** A research-line responsible (PI/CO-PI): code, name, abbreviation. */
export interface LineMemberDto {
  code: string;
  name: string;
  abbreviation?: string | null;
}

export interface TeamDto {
  code: string;
  name: string;
  abbreviation?: string | null;
  observations?: string | null;
  researchLineCode?: string | null;
  researchLineName?: string | null;
  researchLineAbbreviation?: string | null;
  pis: TeamMemberDto[];
  copis: TeamMemberDto[];
  resources: TeamMemberDto[];
}

export interface ResearchLineDto {
  code: string;
  name: string;
  abbreviation?: string | null;
  observations?: string | null;
  pis: LineMemberDto[];
  copis: LineMemberDto[];
}

/** Teams + research lines, with the source they came from. */
export interface TeamsOverviewDto {
  teams: TeamDto[];
  researchLines: ResearchLineDto[];
  source: 'rest' | 'sql' | 'stub' | 'hybrid' | 'none';
  /** ISO timestamp of the last successful sync into the portal DB, or null. */
  syncedAt?: string | null;
  /** Present when the SISTRADE read failed. */
  error?: string | null;
}

/** Result of syncing teams/lines from SISTRADE into the portal DB. */
export interface TeamsSyncResultDto {
  teams: number;
  researchLines: number;
  source: TeamsOverviewDto['source'];
  syncedAt: string | null;
  error?: string | null;
}

/** Scheduled teams-sync settings (Definições → Integração SISTRADE). */
export interface TeamsSyncConfigDto {
  /** When true, the teams/lines are synced automatically. */
  enabled: boolean;
  /** How often to sync, in hours (min 1). */
  intervalHours: number;
  /** ISO timestamp of the last successful sync, or null. */
  lastSyncAt?: string | null;
}

export interface UpdateTeamsSyncConfigInput {
  enabled?: boolean;
  intervalHours?: number;
}

/** One affiliation on a scientific article. */
export interface ArticleAffiliationDto {
  name: string;
  city?: string | null;
  country?: string | null;
}

/** A CIIMAR collaborator an article is attributed to (matched by ORCID). */
export interface ArticleAuthorDto {
  personId: string;
  code: string | null;
  name: string;
}

/** A scientific article (publication) mirrored from Scopus. */
export interface ArticleDto {
  /** Scopus ID (e.g. "105049400716"); the stable primary key. */
  scopusId: string;
  /** Scopus EID (e.g. "2-s2.0-105049400716"). */
  eid?: string | null;
  title: string;
  /** First author as Scopus reports it (e.g. "Sousa C."). */
  creator?: string | null;
  /** Journal / publication name. */
  publicationName?: string | null;
  issn?: string | null;
  volume?: string | null;
  /** Page range or article number. */
  pages?: string | null;
  doi?: string | null;
  pubmedId?: string | null;
  /** ISO cover date (e.g. "2027-03-01"). */
  coverDate?: string | null;
  /** Publication year, derived from the cover date. */
  year?: number | null;
  /** Publication month 1–12, derived from the cover date. */
  month?: number | null;
  citedByCount?: number | null;
  /** Scopus subtype code (e.g. "ar") and its description (e.g. "Article"). */
  typeCode?: string | null;
  typeDescription?: string | null;
  aggregationType?: string | null;
  openAccess: boolean;
  /** The public Scopus record URL. */
  scopusUrl?: string | null;
  affiliations: ArticleAffiliationDto[];
  /** CIIMAR collaborators this article is attributed to (by ORCID). */
  authors: ArticleAuthorDto[];
  /** Journal CiteScore quartile for the article's year (Q1 best), or null. */
  quartile?: string | null;
  /** The journal's CiteScore percentile (0–100) the quartile derives from. */
  citeScorePercentile?: number | null;
}

/** Distinct values offered for filtering the article list. */
export interface ArticleFacetsDto {
  years: number[];
  /** Months (1–12) that have at least one article. */
  months: number[];
  types: string[];
  /** Teams that have at least one member with articles. */
  teams: { code: string; name: string }[];
  /** Collaborators that have at least one article (for the person filter). */
  people: { personId: string; code: string | null; name: string }[];
  /** Journal quartiles present in the mirror (subset of Q1–Q4). */
  quartiles: string[];
}

/** The articles the portal holds in its own DB, plus metadata. */
export interface ArticlesOverviewDto {
  /** The requested page of articles (server-side filtered + paginated). */
  articles: ArticleDto[];
  /** How many articles match the current filters across the whole mirror. */
  total: number;
  /** Total articles reported by Scopus at the last sync (may exceed stored). */
  totalReported?: number | null;
  facets: ArticleFacetsDto;
  source: 'rest' | 'stub' | 'none';
  /** ISO timestamp of the last successful sync into the portal DB, or null. */
  syncedAt?: string | null;
  /** Present when the Scopus read failed. */
  error?: string | null;
}

/** Result of syncing articles from Scopus into the portal DB. */
export interface ArticlesSyncResultDto {
  articles: number;
  totalReported: number | null;
  source: ArticlesOverviewDto['source'];
  syncedAt: string | null;
  error?: string | null;
}

/** Live progress of a background articles sync. */
export interface ArticlesSyncStatusDto {
  running: boolean;
  /** Articles fetched so far this run. */
  fetched: number;
  /** Total reported by Scopus (known after the first page), or null. */
  total: number | null;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
}

/** Scheduled articles-sync settings (Definições → Produção Científica). */
export interface ArticlesSyncConfigDto {
  enabled: boolean;
  /** How often to sync, in hours (min 1). */
  intervalHours: number;
  lastSyncAt?: string | null;
}

export interface UpdateArticlesSyncConfigInput {
  enabled?: boolean;
  intervalHours?: number;
}
